import { createElement, type ReactNode } from "react";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from "@/lib/i18n-locales";
import { baseMetadata } from "@/lib/metadata";
import {
  getLocaleAlternates,
  getLocalizedCanonical,
  getLocalizedPath,
} from "@/lib/seo/locale-meta";
import { getMessagesForLocale, normalizeLocale } from "./i18n-runtime";

type PageModule = {
  default: (props?: any) => ReactNode | Promise<ReactNode>;
  metadata?: Record<string, any>;
  generateMetadata?: (
    props?: any,
  ) => Record<string, any> | Promise<Record<string, any>>;
};
type LayoutModule = {
  metadata?: Record<string, any>;
  generateMetadata?: (
    props?: any,
  ) => Record<string, any> | Promise<Record<string, any>>;
};

const allPageLoaders = import.meta.glob<PageModule>("../app/**/page.tsx");
const allLayoutLoaders = import.meta.glob<LayoutModule>("../app/**/layout.tsx");
const pageLoaders = Object.fromEntries(
  Object.entries(allPageLoaders).filter(([key]) =>
    key.includes("/app/[locale]/"),
  ),
);
const layoutLoaders = Object.fromEntries(
  Object.entries(allLayoutLoaders).filter(([key]) =>
    key.includes("/app/[locale]/"),
  ),
);
const skippedSourceRoutes = new Set([
  "/_status_old",
  "/old-status-redirect",
  "/status-redirect",
]);
const asyncPageKeys = new Set([
  "../app/[locale]/page.tsx",
  "../app/[locale]/pricing/page.tsx",
  "../app/[locale]/templates/page.tsx",
  "../app/[locale]/templates/[slug]/page.tsx",
  "../app/[locale]/alternativas/page.tsx",
  "../app/[locale]/alternativas/typebot/page.tsx",
  "../app/[locale]/alternativas/manychat/page.tsx",
  "../app/[locale]/alternativas/botconversa/page.tsx",
]);

// These pages depend on live upstream data or canonical external infrastructure.
// Pre-rendering them would freeze build-time failures into production HTML until
// the next deployment. They remain SSR-rendered and cacheable at the Bun edge.
const runtimeRenderedPatterns = new Set([
  "/",
  "/pricing",
  "/templates",
  "/system-status",
  "/alternativas",
  "/alternativas/typebot",
  "/alternativas/manychat",
  "/alternativas/botconversa",
]);

function sourceKeyToPattern(key: string): string {
  const relative = key
    .replace(/^\.\.\/app\/\[locale\]\/?/, "")
    .replace(/\/page\.tsx$/, "")
    .replace(/^page\.tsx$/, "")
    .replace(/^\(spa\)\//, "");
  return relative ? `/${relative}` : "/";
}

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function compilePattern(pattern: string) {
  const names: string[] = [];
  const source = pattern
    .split("/")
    .map((segment) => {
      if (!segment) return "";
      const dynamic = segment.match(/^\[([^\]]+)\]$/);
      if (dynamic) {
        names.push(dynamic[1]);
        return "([^/]+)";
      }
      return escapeRegex(segment);
    })
    .join("/");
  return {
    regex: new RegExp(`^${source === "/" ? "/" : source}/?$`),
    names,
  };
}

const routeEntries = Object.entries(pageLoaders)
  .map(([key, loader]) => {
    const pattern = sourceKeyToPattern(key);
    return { key, loader, pattern, ...compilePattern(pattern) };
  })
  .filter((entry) => !skippedSourceRoutes.has(entry.pattern))
  .sort(
    (a, b) =>
      a.names.length - b.names.length || b.pattern.length - a.pattern.length,
  );

export function resolveLocaleAndPath(pathname: string): {
  locale: SupportedLocale;
  routePath: string;
  hadDefaultLocalePrefix: boolean;
} {
  const clean = pathname.split("?")[0].replace(/\/{2,}/g, "/") || "/";
  const segments = clean.split("/").filter(Boolean);
  const candidate = segments[0];
  if (candidate && SUPPORTED_LOCALES.includes(candidate as SupportedLocale)) {
    const locale = normalizeLocale(candidate);
    const rest = `/${segments.slice(1).join("/")}` || "/";
    return {
      locale,
      routePath: rest === "/" ? "/" : rest.replace(/\/$/, ""),
      hadDefaultLocalePrefix: locale === DEFAULT_LOCALE,
    };
  }
  return {
    locale: DEFAULT_LOCALE,
    routePath: clean === "/" ? "/" : clean.replace(/\/$/, ""),
    hadDefaultLocalePrefix: false,
  };
}

function paramsFor(
  entry: (typeof routeEntries)[number],
  match: RegExpMatchArray,
  locale: SupportedLocale,
) {
  const params: Record<string, string> = { locale };
  entry.names.forEach((name, index) => {
    params[name] = decodeURIComponent(match[index + 1]);
  });
  return params;
}

function deepMerge(base: Record<string, any>, override: Record<string, any>) {
  const merged = { ...base };
  for (const [key, value] of Object.entries(override ?? {})) {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      merged[key] &&
      typeof merged[key] === "object" &&
      !Array.isArray(merged[key])
    ) {
      merged[key] = deepMerge(merged[key], value as Record<string, any>);
    } else {
      merged[key] = value;
    }
  }
  return merged;
}

async function resolveParentMetadata(routePath: string, props: any) {
  let metadata: Record<string, any> = {};
  const segments = routePath.split("/").filter(Boolean);
  for (let index = 1; index <= segments.length; index += 1) {
    const key = `../app/[locale]/${segments.slice(0, index).join("/")}/layout.tsx`;
    const loader = layoutLoaders[key];
    if (!loader) continue;
    const module = await loader();
    const value = module.generateMetadata
      ? await module.generateMetadata(props)
      : (module.metadata ?? {});
    metadata = deepMerge(metadata, value ?? {});
  }
  return metadata;
}

export async function resolvePage(urlValue: string) {
  const url = new URL(urlValue, "https://sendbot.co");
  const { locale, routePath, hadDefaultLocalePrefix } = resolveLocaleAndPath(
    url.pathname,
  );
  const entry = routeEntries.find((candidate) =>
    candidate.regex.test(routePath),
  );
  if (!entry) return null;
  const match = routePath.match(entry.regex);
  if (!match) return null;
  const params = paramsFor(entry, match, locale);
  const props = {
    params: Promise.resolve(params),
    searchParams: Object.fromEntries(url.searchParams.entries()),
  };
  const module = await entry.loader();
  const element = asyncPageKeys.has(entry.key)
    ? await module.default(props)
    : createElement(module.default as any, props);

  const parentMetadata = await resolveParentMetadata(routePath, props);
  const pageMetadata = module.generateMetadata
    ? await module.generateMetadata(props)
    : (module.metadata ?? {});
  let metadata = deepMerge(baseMetadata as Record<string, any>, parentMetadata);
  metadata = deepMerge(metadata, pageMetadata ?? {});
  const isTemplateDetail = /^\/templates\/[^/]+$/.test(routePath);
  // API-fed template details are not translated per locale yet.
  const canonical = getLocalizedCanonical(
    isTemplateDetail ? DEFAULT_LOCALE : locale,
    routePath,
  );
  metadata.alternates = {
    ...(metadata.alternates ?? {}),
    canonical,
    languages: isTemplateDetail ? {} : getLocaleAlternates(routePath),
  };
  if (isTemplateDetail && locale !== DEFAULT_LOCALE) {
    metadata.robots = { index: false, follow: true };
  }
  metadata.openGraph = { ...(metadata.openGraph ?? {}), url: canonical };

  return {
    locale,
    routePath,
    hadDefaultLocalePrefix,
    params,
    messages: await getMessagesForLocale(locale),
    element,
    metadata,
    canonicalPath: getLocalizedPath(locale, routePath),
  };
}

function getIndexableStaticPatterns(): string[] {
  return routeEntries
    .filter((entry) => entry.names.length === 0)
    .map((entry) => entry.pattern)
    .filter((pattern) => pattern !== "/system-status");
}

export function getPrerenderPaths(): string[] {
  const prerenderPatterns = getIndexableStaticPatterns().filter(
    (pattern) => !runtimeRenderedPatterns.has(pattern),
  );

  return SUPPORTED_LOCALES.flatMap((locale) =>
    prerenderPatterns.map((path) => getLocalizedPath(locale, path)),
  ).filter((value, index, all) => all.indexOf(value) === index);
}

export function getSitemapEntries() {
  // Do not claim that every page changed at build time.
  return SUPPORTED_LOCALES.flatMap((locale) =>
    getIndexableStaticPatterns().map((path) => ({
      url: `https://sendbot.co${getLocalizedPath(locale, path) === "/" ? "" : getLocalizedPath(locale, path)}`,
    })),
  ).filter(
    (entry, index, all) =>
      all.findIndex((candidate) => candidate.url === entry.url) === index,
  );
}
